import _styled2 from "@emotion/styled/base";

function _extends2() { _extends2 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends2.apply(this, arguments); }

import { useColorMode } from '@chakra-ui/color-mode';
export * from '@chakra-ui/color-mode';
import { toCSSVar, css, propNames, isStyleProp, resolveStyleConfig } from '@chakra-ui/styled-system';
export * from '@chakra-ui/styled-system';
import { ThemeProvider as ThemeProvider$1, Global, ThemeContext } from '@emotion/react';
export { keyframes } from '@emotion/react';
import { memoizedGet, runIfFn, omit, filterUndefined, mergeWith, objectFilter } from '@chakra-ui/utils';
import * as React from 'react';
import { useRef, useMemo } from 'react';
import isEqual from 'react-fast-compare';
import { createContext } from '@chakra-ui/react-utils';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

var ThemeProvider = function ThemeProvider(props) {
  var cssVarsRoot = props.cssVarsRoot,
      theme = props.theme,
      children = props.children;
  var computedTheme = React.useMemo(function () {
    return toCSSVar(theme);
  }, [theme]);
  return /*#__PURE__*/React.createElement(ThemeProvider$1, {
    theme: computedTheme
  }, /*#__PURE__*/React.createElement(CSSVars, {
    root: cssVarsRoot
  }), children);
};

var CSSVars = function CSSVars(_ref) {
  var _ref$root = _ref.root,
      root = _ref$root === void 0 ? ":host, :root" : _ref$root;
  /**
   * Append color mode selector to allow semantic tokens to change according to the color mode
   */

  var selector = [root, "[data-theme]"].join(",");
  return /*#__PURE__*/React.createElement(Global, {
    styles: function styles(theme) {
      var _ref2;

      return _ref2 = {}, _ref2[selector] = theme.__cssVars, _ref2;
    }
  });
};

function useTheme() {
  var theme = React.useContext(ThemeContext);

  if (!theme) {
    throw Error("useTheme: `theme` is undefined. Seems you forgot to wrap your app in `<ChakraProvider />` or `<ThemeProvider />`");
  }

  return theme;
}

var _createContext = createContext({
  name: "StylesContext",
  errorMessage: "useStyles: `styles` is undefined. Seems you forgot to wrap the components in `<StylesProvider />` "
}),
    StylesProvider = _createContext[0],
    useStyles = _createContext[1];
/**
 * Applies styles defined in `theme.styles.global` globally
 * using emotion's `Global` component
 */


var GlobalStyle = function GlobalStyle() {
  var _useColorMode = useColorMode(),
      colorMode = _useColorMode.colorMode;

  return /*#__PURE__*/React.createElement(Global, {
    styles: function styles(theme) {
      var styleObjectOrFn = memoizedGet(theme, "styles.global");
      var globalStyles = runIfFn(styleObjectOrFn, {
        theme: theme,
        colorMode: colorMode
      });
      if (!globalStyles) return undefined;
      var styles = css(globalStyles)(theme);
      return styles;
    }
  });
};

function omitThemingProps(props) {
  return omit(props, ["styleConfig", "size", "variant", "colorScheme"]);
}

function useChakra() {
  var colorModeResult = useColorMode();
  var theme = useTheme();
  return _extends({}, colorModeResult, {
    theme: theme
  });
}

var resolveBreakpointValue = function resolveBreakpointValue(theme, tokenValue, fallbackValue) {
  var _ref, _getValue;

  if (tokenValue === null) return tokenValue;

  var getValue = function getValue(val) {
    var _theme$__breakpoints, _theme$__breakpoints$;

    return (_theme$__breakpoints = theme.__breakpoints) == null ? void 0 : (_theme$__breakpoints$ = _theme$__breakpoints.asArray) == null ? void 0 : _theme$__breakpoints$[val];
  };

  return (_ref = (_getValue = getValue(tokenValue)) != null ? _getValue : getValue(fallbackValue)) != null ? _ref : fallbackValue;
}; // inspired from ./css.ts : resolveTokenValue


var resolveTokenValue = function resolveTokenValue(theme, tokenValue, fallbackValue) {
  var _ref2, _getValue2;

  if (tokenValue == null) return tokenValue;

  var getValue = function getValue(val) {
    var _theme$__cssMap, _theme$__cssMap$val;

    return (_theme$__cssMap = theme.__cssMap) == null ? void 0 : (_theme$__cssMap$val = _theme$__cssMap[val]) == null ? void 0 : _theme$__cssMap$val.value;
  };

  return (_ref2 = (_getValue2 = getValue(tokenValue)) != null ? _getValue2 : getValue(fallbackValue)) != null ? _ref2 : fallbackValue;
};

function useToken(scale, token, fallback) {
  var theme = useTheme();

  if (Array.isArray(token)) {
    var fallbackArr = [];

    if (fallback) {
      fallbackArr = Array.isArray(fallback) ? fallback : [fallback];
    }

    return token.map(function (token, index) {
      var _fallbackArr$index2;

      if (scale === "breakpoints") {
        var _fallbackArr$index;

        return resolveBreakpointValue(theme, token, (_fallbackArr$index = fallbackArr[index]) != null ? _fallbackArr$index : token);
      }

      var path = scale + "." + token;
      return resolveTokenValue(theme, path, (_fallbackArr$index2 = fallbackArr[index]) != null ? _fallbackArr$index2 : token);
    });
  }

  if (scale === "breakpoints") {
    return resolveBreakpointValue(theme, token, fallback);
  }

  var path = scale + "." + token;
  return resolveTokenValue(theme, path, fallback);
}

function useProps(themeKey, props) {
  var _theme$components, _styleConfig$defaultP;

  var _useChakra = useChakra(),
      theme = _useChakra.theme,
      colorMode = _useChakra.colorMode;

  var styleConfig = props.styleConfig || ((_theme$components = theme.components) == null ? void 0 : _theme$components[themeKey]);
  var defaultProps = (_styleConfig$defaultP = styleConfig == null ? void 0 : styleConfig.defaultProps) != null ? _styleConfig$defaultP : {};

  var propsWithDefault = _extends({}, defaultProps, filterUndefined(props));

  var stylesRef = useRef({});
  var mergedProps = mergeWith({}, propsWithDefault, {
    theme: theme,
    colorMode: colorMode
  });
  var memoizedStyles = useMemo(function () {
    if (styleConfig) {
      var _styleConfig$baseStyl, _styleConfig$variants, _styleConfig$variants2, _styleConfig$sizes, _styleConfig$sizes2;

      var baseStyles = runIfFn((_styleConfig$baseStyl = styleConfig.baseStyle) != null ? _styleConfig$baseStyl : {}, mergedProps);
      var variants = runIfFn((_styleConfig$variants = (_styleConfig$variants2 = styleConfig.variants) == null ? void 0 : _styleConfig$variants2[mergedProps.variant]) != null ? _styleConfig$variants : {}, mergedProps);
      var sizes = runIfFn((_styleConfig$sizes = (_styleConfig$sizes2 = styleConfig.sizes) == null ? void 0 : _styleConfig$sizes2[mergedProps.size]) != null ? _styleConfig$sizes : {}, mergedProps);
      var styles = mergeWith(baseStyles, sizes, variants);

      if (styleConfig.parts) {
        styleConfig.parts.forEach(function (part) {
          var _styles$part;

          styles[part] = (_styles$part = styles[part]) != null ? _styles$part : {};
        });
      }

      var isStyleEqual = isEqual(stylesRef.current, styles);

      if (!isStyleEqual) {
        stylesRef.current = styles;
      }
    }

    return stylesRef.current;
  }, [styleConfig, mergedProps]);
  return {
    styles: memoizedStyles,
    props: omitThemingProps(propsWithDefault)
  };
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}
/**
 * List of props for emotion to omit from DOM.
 * It mostly consists of Chakra props
 */


var allPropNames = new Set([].concat(propNames, ["textStyle", "layerStyle", "apply", "noOfLines", "focusBorderColor", "errorBorderColor", "as", "__css", "css", "sx"]));
/**
 * htmlWidth and htmlHeight is used in the <Image />
 * component to support the native `width` and `height` attributes
 *
 * https://github.com/chakra-ui/chakra-ui/issues/149
 */

var validHTMLProps = new Set(["htmlWidth", "htmlHeight", "htmlSize"]);

var shouldForwardProp = function shouldForwardProp(prop) {
  return validHTMLProps.has(prop) || !allPropNames.has(prop);
};

var _excluded$1 = ["theme", "css", "__css", "sx"],
    _excluded2 = ["baseStyle"];
/**
 * Style resolver function that manages how style props are merged
 * in combination with other possible ways of defining styles.
 *
 * For example, take a component defined this way:
 * ```jsx
 * <Box fontSize="24px" sx={{ fontSize: "40px" }}></Box>
 * ```
 *
 * We want to manage the priority of the styles properly to prevent unwanted
 * behaviors. Right now, the `sx` prop has the highest priority so the resolved
 * fontSize will be `40px`
 */

var toCSSObject = function toCSSObject(_ref) {
  var baseStyle = _ref.baseStyle;
  return function (props) {
    props.theme;

    var cssProp = props.css,
        __css = props.__css,
        sx = props.sx,
        rest = _objectWithoutPropertiesLoose(props, _excluded$1);

    var styleProps = objectFilter(rest, function (_, prop) {
      return isStyleProp(prop);
    });
    var finalBaseStyle = runIfFn(baseStyle, props);
    var finalStyles = Object.assign({}, __css, finalBaseStyle, filterUndefined(styleProps), sx);
    var computedCSS = css(finalStyles)(props.theme);
    return cssProp ? [computedCSS, cssProp] : computedCSS;
  };
};

function styled(component, options) {
  var _ref2 = options != null ? options : {},
      baseStyle = _ref2.baseStyle,
      styledOptions = _objectWithoutPropertiesLoose(_ref2, _excluded2);

  if (!styledOptions.shouldForwardProp) {
    styledOptions.shouldForwardProp = shouldForwardProp;
  }

  var styleObject = toCSSObject({
    baseStyle: baseStyle
  });
  return /*#__PURE__*/_styled2(component, process.env.NODE_ENV === "production" ? _extends2({}, {
    target: "e1k4it830"
  }, styledOptions) : _extends2({}, {
    target: "e1k4it830",
    label: "styled"
  }, styledOptions))(styleObject, process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */");
}
/**
 * All credit goes to Chance (Reach UI), Haz (Reakit) and (fluentui)
 * for creating the base type definitions upon which we improved on
 */


function forwardRef(component) {
  return /*#__PURE__*/React.forwardRef(component);
}

var _excluded = ["styleConfig"];

function useStyleConfigImpl(themeKey, props) {
  var _styleConfig$defaultP;

  if (props === void 0) {
    props = {};
  }

  var _props = props,
      styleConfigProp = _props.styleConfig,
      rest = _objectWithoutPropertiesLoose(_props, _excluded);

  var _useChakra = useChakra(),
      theme = _useChakra.theme,
      colorMode = _useChakra.colorMode;

  var themeStyleConfig = memoizedGet(theme, "components." + themeKey);
  var styleConfig = styleConfigProp || themeStyleConfig;
  var mergedProps = mergeWith({
    theme: theme,
    colorMode: colorMode
  }, (_styleConfig$defaultP = styleConfig == null ? void 0 : styleConfig.defaultProps) != null ? _styleConfig$defaultP : {}, filterUndefined(omit(rest, ["children"])));
  /**
   * Store the computed styles in a `ref` to avoid unneeded re-computation
   */

  var stylesRef = useRef({});

  if (styleConfig) {
    var getStyles = resolveStyleConfig(styleConfig);
    var styles = getStyles(mergedProps);
    var isStyleEqual = isEqual(stylesRef.current, styles);

    if (!isStyleEqual) {
      stylesRef.current = styles;
    }
  }

  return stylesRef.current;
}

function useStyleConfig(themeKey, props) {
  if (props === void 0) {
    props = {};
  }

  return useStyleConfigImpl(themeKey, props);
}

function useMultiStyleConfig(themeKey, props) {
  if (props === void 0) {
    props = {};
  }

  return useStyleConfigImpl(themeKey, props);
}

function factory() {
  var cache = new Map();
  return new Proxy(styled, {
    /**
     * @example
     * const Div = chakra("div")
     * const WithChakra = chakra(AnotherComponent)
     */
    apply: function apply(target, thisArg, argArray) {
      return styled.apply(void 0, argArray);
    },

    /**
     * @example
     * <chakra.div />
     */
    get: function get(_, element) {
      if (!cache.has(element)) {
        cache.set(element, styled(element));
      }

      return cache.get(element);
    }
  });
}

var chakra = factory();
export { CSSVars, GlobalStyle, StylesProvider, ThemeProvider, chakra, forwardRef, omitThemingProps, shouldForwardProp, styled, toCSSObject, useChakra, useMultiStyleConfig, useProps, useStyleConfig, useStyles, useTheme, useToken };